import { NameAnd } from "@me8eon/record_utils";
import { ErrorsOr, isErrors, isValue, LogOptionOrString } from "@me8eon/errors";
import { LogAnd } from "@me8eon/errors/src/log.options";
import { AiClient, AiOptions } from "./ai.client";
import { normaliseMessage, Message } from "@me8eon/messages";
import { safeArray } from "@me8eon/arrays";
import { AiTool } from "./ai.tools";
import { Validator } from "@me8eon/validate";
import { nameWithOptions } from "@me8eon/string_utils";

type ExecuteAiToolResponse = {
    _meta?: {
        skipAiSummary?: boolean
        [key: string]: any
    };
    value: string
}

export type AiToolImpl<Args> = {
    type: string
    toolDefn: AiTool
    validateArgs?: Validator<Args>;
    execute: (options: string[], t: Args) => Promise<ExecuteAiToolResponse>;
};

export type AiToolsRepository = NameAnd<AiToolImpl<any>>;

export const callAiTool =
    (repo: AiToolsRepository, legalTools: string[]) => {
        const legalToolNames = Object.keys(repo).sort();
        return async (name: string, options: string[], args: unknown): Promise<LogAnd<ErrorsOr<ExecuteAiToolResponse>>> => {
            const tool = repo[name];
            if (!tool) {
                return {
                    errors: [`Tool ${name} not found. Legal values are: ${legalToolNames}`],
                    log: { whatHappened: "tool.not.found", params: { name }, severity: "error" },
                };
            }
            if (!legalToolNames.includes(name)) {
                return {
                    errors: [`Tool ${name} not allowed (it is known). Legal values are: ${legalToolNames}`],
                    log: { whatHappened: "tool.illegal", params: { name, legalTools }, severity: "error" },
                };
            }

            if (tool.validateArgs) {
                try {
                    const errs = tool.validateArgs("args")(args);
                    if (errs?.length) {
                        return {
                            errors: errs,
                            log: { whatHappened: "tool.validation.failed", params: { name, errs }, severity: "error" },
                        };
                    }
                } catch (e: any) {
                    return {
                        errors: [String(e?.message ?? "validation failed")],
                        log: {
                            whatHappened: "tool.validation.exception",
                            params: { name, e: String(e) },
                            severity: "error"
                        },
                    };
                }
            }

            try {
                const result = await tool.execute(options, args);
                if (typeof result?.value !== "string") {
                    return {
                        errors: ["Tool result.value is not string"],
                        log: { whatHappened: "tool.result.not.json", params: { name, result }, severity: "error" },
                    };
                }
                return {
                    value: result,
                    log: { whatHappened: "tool.executed", params: { name, args } },
                };
            } catch (e: any) {
                return {
                    errors: [String(e?.message ?? "tool execution error")],
                    log: { whatHappened: "tool.execution.failed", params: { name, e: String(e) }, severity: "error" },
                };
            }
        };
    };

export type AiAndToolOptions = AiOptions & {
    toolRepo: AiToolsRepository,
    legalTools: string[]
    maxLoops?: number
    toolSelectionModel?: string
    languageReminder?: Message
}

export async function callAiAndAnyTools(aiClient: AiClient, toolRepo: AiToolsRepository, prompts: Message[], options: AiAndToolOptions): Promise<LogAnd<ErrorsOr<Message[]>>> {
    const { toolRepo: repo, legalTools = [], maxLoops = 5, stream, toolSelectionModel } = options;

    const toolNameAndOptions = legalTools.map(nameWithOptions);

    function findTools(): AiTool[] {
        return toolNameAndOptions.map(t => {
            const tool = toolRepo[t.name];
            if (!tool) throw new Error(`Tool ${JSON.stringify(t)}} not found in repo. Legal tools are ${Object.keys(toolRepo).sort()}`);
            return tool.toolDefn;
        });
    }

    const tools = findTools();
    const messages: Message[] = [];
    const log: LogOptionOrString[] = [];

    let expectingMoreTools = tools.length > 0;

    for (let i = 0; i < maxLoops; i++) {
        const effectiveModel = (toolSelectionModel && expectingMoreTools) ? toolSelectionModel : undefined;
        const loopOptions: AiOptions = effectiveModel
            ? { ...options, model: effectiveModel, tools }
            : { ...options, tools };

        // languageReminder is sent to the model on every call but never persisted, so it can't leak into chat history.
        const callMessages = options.languageReminder
            ? [...prompts, ...messages, options.languageReminder]
            : [...prompts, ...messages];
        const aiResult = await aiClient(callMessages, loopOptions);
        if (isErrors(aiResult)) return aiResult;

        const msg = aiResult.value.message;
        messages.push(normaliseMessage(msg));

        const toolCalls = aiResult.value.toolCalls;
        let skipAiSummaryInAnyToolResults = false;

        if (toolCalls?.length) {
            expectingMoreTools = true;
            for (const { tool_name, tool_call_id, tool_args } of toolCalls) {
                await stream?.sink?.({
                    type: "tool-call-start",
                    toolName: tool_name,
                    toolCallId: tool_call_id
                });

                const optionsForTool = toolNameAndOptions.find(t => t.name === tool_name)?.options || [];
                const toolResult = await callAiTool(repo, legalTools)(tool_name, optionsForTool, tool_args);

                log.push(...safeArray(toolResult.log));

                const isErrorsResult = isErrors(toolResult);
                const values = isValue(toolResult);

                if (isErrorsResult || !values) {
                    messages.push({
                        role: "tool",
                        content: `Error calling tool ${tool_name}: ${safeArray(toolResult.errors).join(", ")}`,
                        tool_call_id
                    });

                    await stream?.sink?.({
                        type: "tool-call-end",
                        toolName: tool_name,
                        toolCallId: tool_call_id
                    });

                    continue;
                }

                const results = toolResult as unknown as { value: ExecuteAiToolResponse };
                const content = JSON.stringify(results.value, null, 2);

                messages.push({ role: "tool", content, tool_call_id });

                if (results?.value?._meta?.skipAiSummary) {
                    skipAiSummaryInAnyToolResults = true;
                }

                await stream?.sink?.({
                    type: "tool-call-end",
                    toolName: tool_name,
                    toolCallId: tool_call_id
                });
            }

            if (skipAiSummaryInAnyToolResults) {
                return { value: messages, log };
            }
        } else {
            expectingMoreTools = false;
            return { value: messages, log };
        }
    }

    log.push({ whatHappened: "maxLoops.reached", params: { maxLoops }, severity: "error" });
    return { errors: ["Max tool call loops reached"], log };
}